import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Printer, Gift } from "lucide-react";
import api from "@/lib/api";

export default function GiftCertificate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { data: gift, isLoading, isError } = useQuery({
    queryKey: ["gift-cert", token],
    queryFn: () => api.get(`/gift/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });
  const redeemUrl = `${window.location.origin}/redeem?token=${token}`;

  if (!token || isError) {
    return <div className="min-h-[70vh] grid place-items-center px-6"><p className="text-[#4A5D6B]" data-testid="cert-invalid">This certificate link isn't valid.</p></div>;
  }
  if (isLoading) return <div className="min-h-[70vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div>;

  return (
    <div className="min-h-[80vh] bg-[#F5F1EA] py-10 px-4" data-testid="gift-certificate-page">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #cert, #cert * { visibility: visible !important; }
        #cert { position: absolute; top: 0; left: 0; width: 100%; box-shadow: none !important; }
        .no-print { display: none !important; }
      }`}</style>

      <div className="max-w-2xl mx-auto mb-5 flex justify-center no-print">
        <button onClick={() => window.print()} data-testid="cert-print-btn" className="eyw-btn">
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>

      <div id="cert" className="max-w-2xl mx-auto bg-white p-10 sm:p-14 shadow-xl" style={{ border: "2px solid #144F75", outline: "6px solid #fff", outlineOffset: "-14px" }}>
        <div className="text-center border-b border-[#E5E0D8] pb-6">
          <span className="grid place-items-center h-14 w-14 rounded-full bg-[#D98E73] text-white mx-auto mb-4"><Gift size={24} /></span>
          <p className="eyw-label tracking-widest">Enhancing Your World</p>
          <h1 className="font-serif text-4xl text-[#144F75] mt-2">Gift Certificate</h1>
        </div>

        <div className="text-center py-8">
          <p className="text-[#8FA89B] text-sm">Presented to</p>
          <p className="font-serif text-3xl text-[#1A242B] mt-1" data-testid="cert-recipient">{gift.recipient_name || gift.recipient_email || "A cherished friend"}</p>

          <div className="my-8">
            <p className="font-serif text-2xl text-[#D98E73]" data-testid="cert-package">{gift.package_name}</p>
            <p className="text-[#4A5D6B] mt-1"><b>{gift.sessions}</b> wellness session{gift.sessions === 1 ? "" : "s"}</p>
          </div>

          {gift.gift_message && <p className="italic text-[#4A5D6B] max-w-md mx-auto">“{gift.gift_message}”</p>}
          {gift.giver_name && <p className="text-[#1A242B] mt-6">With love, from <b>{gift.giver_name}</b></p>}
        </div>

        <div className="text-center border-t border-[#E5E0D8] pt-6">
          <p className="text-sm text-[#4A5D6B]">To redeem, visit</p>
          <p className="text-sm text-[#144F75] font-medium break-all" data-testid="cert-redeem-url">{redeemUrl}</p>
          <p className="text-xs text-[#8FA89B] mt-3">Reserved for {gift.recipient_email} · Questions? 859-979-5505</p>
        </div>
      </div>
    </div>
  );
}
